import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { REGION_NAMES } from "@/lib/regions";
import { Percent, Wrench, TrendingUp, Map, HeartHandshake, CheckCircle2 } from "lucide-react";

const PERKS = [
  [Percent, "65% revenue share", "Keep 65% of what your region's stores pay (after Stripe fees) — Fiver Deal fees, $2 adverts, wallet top-ups and boosts. Paid monthly."],
  [Wrench, "Zero technical overhead", "We provide and manage 100% of the website, hosting, software, payments and platform updates."],
  [TrendingUp, "Build a sellable asset", "Grow recurring monthly income and real equity in your own regional digital business."],
  [Map, "Your own regional site", "yourregion.markdowns.nz with your regional logo, welcome line and contact details."],
  [HeartHandshake, "Full support", "Turnkey setup and ongoing central guidance. No tech background needed — just local connections and drive."],
];

function InterestForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", region: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/partner-interest", f); setDone(true); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (done) return <div className="text-center py-10 space-y-2" data-testid="partner-success"><CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto" /><div className="font-display font-bold text-xl">Thanks, {f.name}!</div><p className="text-slate-600">We&apos;ll be in touch about {f.region} soon.</p></div>;
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="partner-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Your name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="partner-name" /></div>
        <div><Label>Email</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="partner-email" /></div>
        <div><Label>Phone (optional)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="partner-phone" /></div>
        <div><Label>Region you&apos;d like to run</Label>
          <Select value={f.region} onValueChange={(v) => setF({ ...f, region: v })}>
            <SelectTrigger data-testid="partner-region"><SelectValue placeholder="Pick a region" /></SelectTrigger>
            <SelectContent>{REGION_NAMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Tell us a bit about yourself (optional)</Label><Textarea rows={4} maxLength={2000} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Your local networks, experience, why this region…" data-testid="partner-message" /></div>
      <Button type="submit" disabled={busy || !f.region} className="w-full h-11 bg-emerald-800 hover:bg-emerald-900" data-testid="partner-submit">{busy ? "Sending…" : "Send expression of interest"}</Button>
    </form>
  );
}

export default function Partners() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7 space-y-6">
        <div className="inline-block text-xs font-black uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1.5 rounded-full">Become a Regional Partner</div>
        <h1 className="text-4xl sm:text-5xl font-display font-extrabold leading-tight" data-testid="partners-title">Run Markdowns in <span className="text-emerald-800">your region</span>.</h1>
        <p className="text-slate-600 text-base sm:text-lg">Looking for a path into self-employment? Markdowns is expanding across all 16 regions of Aotearoa. We&apos;re looking for self-motivated local networkers and self-starters to sign up stores and grow their region.</p>
        <div className="space-y-4">
          {PERKS.map(([Icon, title, text]) => (
            <div key={title} className="flex gap-4"><div className="w-11 h-11 shrink-0 rounded-xl bg-emerald-800 text-white grid place-items-center"><Icon className="w-5 h-5" /></div>
              <div><div className="font-display font-bold">{title}</div><p className="text-sm text-slate-600">{text}</p></div></div>
          ))}
        </div>
        <p className="text-sm text-slate-500">Prefer to talk first? Email <a href="mailto:blurblive@outlook.com" className="text-emerald-800 underline underline-offset-4" data-testid="partners-contact-email">blurblive@outlook.com</a> about territory availability.</p>
      </div>
      <div className="lg:col-span-5"><Card className="border-emerald-200 shadow-lg lg:sticky lg:top-28"><CardContent className="pt-6"><InterestForm /></CardContent></Card></div>
    </div>
  );
}
